import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../App";
import axios from "axios";
import {
  Plus, Search, ChevronLeft, Loader2, CheckCircle2, AlertTriangle,
  ClipboardList, FileText, Eye, Shield, Users, Layers, Package,
  Send, ArrowRight, Clock, Zap, X, ChevronDown, ChevronUp,
  MessageSquare, UserCheck, Lock, RefreshCw, Download,
  Pen, ExternalLink, Filter
} from "lucide-react";

const AUDIT_STATUSES = ["draft", "planned", "in_progress", "daily_wrap", "closeout", "closed", "archived"];
const STATUS_COLORS = {
  draft: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  planned: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  in_progress: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  daily_wrap: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  closeout: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  closed: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  archived: "bg-slate-700/20 text-slate-500 border-slate-700/30",
};
const RISK_COLORS = { low: "text-green-400", medium: "text-amber-400", high: "text-orange-400", critical: "text-red-400" };
const FINDING_TYPE_COLORS = {
  observation: "bg-blue-500/15 text-blue-400",
  ofi: "bg-cyan-500/15 text-cyan-400",
  minor_nonconformance: "bg-amber-500/15 text-amber-400",
  major_nonconformance: "bg-orange-500/15 text-orange-400",
  critical_nonconformance: "bg-red-500/15 text-red-400",
};

const AuditConductorPage = () => {
  const { t } = useTranslation('audit');
  const { user } = useAuth();
  const [view, setView] = useState("list"); // list | wizard | detail
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => { loadAudits(); }, []);

  const loadAudits = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/audit-conductor/audits`, { withCredentials: true });
      setAudits(res.data.audits || []);
    } catch (e) { console.error("Load audits failed:", e); }
    finally { setLoading(false); }
  };

  const openAudit = (audit) => { setSelectedAudit(audit); setActiveTab("overview"); setView("detail"); };
  const goBack = () => { setView("list"); loadAudits(); };

  if (view === "wizard") return <AuditWizard onBack={() => { setView("list"); loadAudits(); }} onCreated={(a) => { setSelectedAudit(a); setView("detail"); }} />;
  if (view === "detail" && selectedAudit) return <AuditDetail auditId={selectedAudit.audit_id} onBack={goBack} activeTab={activeTab} setActiveTab={setActiveTab} />;

  return (
    <div className="min-h-screen bg-[#0A1628] text-white p-6" data-testid="audit-conductor-dashboard">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">{t('conductor.title')}</h1>
            <p className="text-sm text-slate-400 mt-1">{t('conductor.subtitle')}</p>
          </div>
          <button onClick={() => setView("wizard")} className="flex items-center gap-2 px-4 py-2.5 bg-[#1A6BCC] hover:bg-[#1558A8] rounded-lg text-sm font-bold transition-colors" data-testid="create-audit-btn">
            <Plus className="w-4 h-4" /> {t('conductor.createAudit')}
          </button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /> {t('conductor.loadingAudits')}</div>
        ) : audits.length === 0 ? (
          <div className="text-center py-16">
            <ClipboardList className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
            <p className="text-slate-400">{t('conductor.noAudits')}</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="audit-list">
            {audits.map(a => (
              <button key={a.audit_id} onClick={() => openAudit(a)} className="w-full text-left p-4 bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-xl hover:border-[#1A6BCC]/40 transition-colors group" data-testid={`audit-${a.audit_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-white group-hover:text-[#00C8E0]">{a.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {a.audit_id} &middot; {a.audit_type} &middot; {(a.standards || []).join(", ")}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs font-bold ${RISK_COLORS[a.risk_level] || ""}`}>{a.risk_level?.toUpperCase()}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${STATUS_COLORS[a.status] || ""}`}>{a.status?.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="flex gap-4 mt-2 text-[10px] text-slate-500">
                  <span>{a.checklist_count || 0} {t('conductor.checklistItems')}</span>
                  <span>{a.findings_count || 0} {t('conductor.findings')}</span>
                  <span>{a.evidence_count || 0} {t('conductor.evidence')}</span>
                  {a.site && <span>{a.site}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════ AUDIT WIZARD ═══════════ */
const AuditWizard = ({ onBack, onCreated }) => {
  const { t } = useTranslation('audit');
  const [step, setStep] = useState(0); // 0=type, 1=scope, 2=standards, 3=risk/team, 4=review
  const [form, setForm] = useState({
    title: "", audit_type: "internal", standards: [], scope: "", site: "",
    product_family: "", process_family: "", criticality_tags: [],
    risk_level: "medium", front_room_enabled: true, back_room_enabled: true,
    scheduled_start: "", scheduled_end: "",
  });
  const [availableStandards, setAvailableStandards] = useState([]);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    axios.get(`${API}/audit-conductor/standards`, { withCredentials: true })
      .then(r => setAvailableStandards(r.data.standards || []))
      .catch(() => {});
  }, []);

  const toggleStandard = (std) => {
    setForm(prev => ({
      ...prev,
      standards: prev.standards.includes(std) ? prev.standards.filter(s => s !== std) : [...prev.standards, std],
    }));
  };

  const createAudit = async () => {
    if (!form.title) return alert(t('conductor.wizard.titleRequired'));
    if (form.standards.length === 0) return alert(t('conductor.wizard.selectStandard'));
    setCreating(true);
    try {
      const res = await axios.post(`${API}/audit-conductor/audits`, form, { withCredentials: true });
      onCreated(res.data);
    } catch (e) { alert(e.response?.data?.detail || t('conductor.wizard.failedCreate')); }
    finally { setCreating(false); }
  };

  const steps = ["Type", "Scope", "Standards", "Risk & Team", "Review"];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white p-6" data-testid="audit-wizard">
      <div className="max-w-2xl mx-auto">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white mb-6" data-testid="wizard-back"><ChevronLeft className="w-4 h-4" /> {t('conductor.wizard.back')}</button>
        <h1 className="text-xl font-bold mb-2">{t('conductor.wizard.title')}</h1>
        {/* Step indicator */}
        <div className="flex gap-1 mb-8">
          {steps.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} className={`flex-1 py-1.5 text-[10px] font-bold rounded ${i === step ? "bg-[#1A6BCC] text-white" : i < step ? "bg-emerald-500/20 text-emerald-400" : "bg-[#132340] text-slate-500"}`}>{s}</button>
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs text-slate-400">{t('conductor.wizard.auditTitle')}</span>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#1A6BCC] focus:outline-none" placeholder="ISO 13485 Internal Audit - Q2 2026" data-testid="wizard-title" />
            </label>
            <label className="block">
              <span className="text-xs text-slate-400">{t('conductor.wizard.auditType')}</span>
              <select value={form.audit_type} onChange={e => setForm(p => ({ ...p, audit_type: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#1A6BCC] focus:outline-none" data-testid="wizard-type">
                {["internal", "external", "supplier", "certification", "surveillance"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
            </label>
            <button onClick={() => setStep(1)} disabled={!form.title} className="w-full py-2.5 bg-[#1A6BCC] rounded-lg text-sm font-bold disabled:opacity-50" data-testid="wizard-next-1">Next: Scope</button>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs text-slate-400">{t('conductor.wizard.scopeDesc')}</span>
              <textarea value={form.scope} onChange={e => setForm(p => ({ ...p, scope: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#1A6BCC] focus:outline-none h-24 resize-none" placeholder="Design control and production for Class II devices" data-testid="wizard-scope" />
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs text-slate-400">{t('conductor.wizard.siteLocation')}</span>
                <input value={form.site} onChange={e => setForm(p => ({ ...p, site: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#1A6BCC] focus:outline-none" data-testid="wizard-site" />
              </label>
              <label className="block">
                <span className="text-xs text-slate-400">{t('conductor.wizard.productFamily')}</span>
                <input value={form.product_family} onChange={e => setForm(p => ({ ...p, product_family: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white focus:border-[#1A6BCC] focus:outline-none" data-testid="wizard-product" />
              </label>
            </div>
            <button onClick={() => setStep(2)} className="w-full py-2.5 bg-[#1A6BCC] rounded-lg text-sm font-bold" data-testid="wizard-next-2">{t('conductor.wizard.nextStandards')}</button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3" data-testid="wizard-standards">
            <p className="text-xs text-slate-400 mb-2">{t('conductor.wizard.selectStandards', { count: form.standards.length })}</p>
            {availableStandards.map(s => (
              <button key={s.standard} onClick={() => toggleStandard(s.standard)} className={`w-full text-left p-3 rounded-lg border transition-colors ${form.standards.includes(s.standard) ? "bg-[#1A6BCC]/15 border-[#1A6BCC]/40" : "bg-[#0D1F38] border-[#1E3A5F]/50 hover:border-[#1E3A5F]"}`} data-testid={`std-${s.standard.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-white">{s.standard}</span>
                    <span className="text-[10px] text-slate-500 ml-2">{s.domain}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-slate-500">{s.clause_count} clauses</span>
                    {form.standards.includes(s.standard) && <CheckCircle2 className="w-4 h-4 text-[#1A6BCC]" />}
                  </div>
                </div>
              </button>
            ))}
            <button onClick={() => setStep(3)} disabled={form.standards.length === 0} className="w-full py-2.5 bg-[#1A6BCC] rounded-lg text-sm font-bold disabled:opacity-50" data-testid="wizard-next-3">{t('conductor.wizard.nextRisk')}</button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-4">
            <label className="block">
              <span className="text-xs text-slate-400">{t('conductor.wizard.riskLevel')}</span>
              <select value={form.risk_level} onChange={e => setForm(p => ({ ...p, risk_level: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white" data-testid="wizard-risk">
                {["low", "medium", "high", "critical"].map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.front_room_enabled} onChange={e => setForm(p => ({ ...p, front_room_enabled: e.target.checked }))} className="rounded" data-testid="wizard-front-room" />
                <span className="text-sm text-white">{t('conductor.wizard.frontRoom')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.back_room_enabled} onChange={e => setForm(p => ({ ...p, back_room_enabled: e.target.checked }))} className="rounded" data-testid="wizard-back-room" />
                <span className="text-sm text-white">{t('conductor.wizard.backRoom')}</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-xs text-slate-400">{t('conductor.wizard.startDate')}</span>
                <input type="date" value={form.scheduled_start} onChange={e => setForm(p => ({ ...p, scheduled_start: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white" data-testid="wizard-start" />
              </label>
              <label className="block">
                <span className="text-xs text-slate-400">{t('conductor.wizard.endDate')}</span>
                <input type="date" value={form.scheduled_end} onChange={e => setForm(p => ({ ...p, scheduled_end: e.target.value }))} className="w-full mt-1 bg-[#0D1F38] border border-[#1E3A5F] rounded-lg px-3 py-2.5 text-sm text-white" data-testid="wizard-end" />
              </label>
            </div>
            <button onClick={() => setStep(4)} className="w-full py-2.5 bg-[#1A6BCC] rounded-lg text-sm font-bold" data-testid="wizard-next-4">{t('conductor.wizard.reviewCreate')}</button>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="wizard-review">
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 space-y-2">
              <div className="text-sm font-bold text-white">{form.title}</div>
              <div className="text-xs text-slate-400">Type: {form.audit_type} | Risk: <span className={RISK_COLORS[form.risk_level] || ""}>{form.risk_level}</span></div>
              {form.scope && <div className="text-xs text-slate-500">{form.scope}</div>}
              <div className="text-xs text-slate-400">Standards: {form.standards.join(", ")}</div>
              {form.site && <div className="text-xs text-slate-500">Site: {form.site}</div>}
              <div className="text-xs text-slate-500">Rooms: {form.front_room_enabled ? "Front" : ""} {form.back_room_enabled ? "Back" : ""}</div>
            </div>
            <button onClick={createAudit} disabled={creating} className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-bold disabled:opacity-50" data-testid="wizard-create">
              {creating ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : null}{t('conductor.wizard.createAudit')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════ AUDIT DETAIL ═══════════ */
const AuditDetail = ({ auditId, onBack, activeTab, setActiveTab }) => {
  const { t } = useTranslation('audit');
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAudit = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}`, { withCredentials: true });
      setAudit(res.data);
    } catch (e) { console.error("Load audit failed:", e); }
    finally { setLoading(false); }
  }, [auditId]);

  useEffect(() => { loadAudit(); }, [loadAudit]);

  if (loading || !audit) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const tabs = [
    { id: "overview", label: "Overview", icon: Eye },
    { id: "checklist", label: "Checklist", icon: ClipboardList },
    { id: "evidence", label: "Evidence", icon: FileText },
    { id: "findings", label: "Findings", icon: AlertTriangle },
    { id: "front_room", label: "Front Room", icon: MessageSquare },
    { id: "back_room", label: "Back Room", icon: Users },
    { id: "daily_wrap", label: "Daily Wrap", icon: Clock },
    { id: "packages", label: "Packages", icon: Package },
    { id: "transfers", label: "Transfers", icon: Send },
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="audit-detail-page">
      {/* Header */}
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="p-2 rounded bg-[#132340] hover:bg-[#1E3A5F]" data-testid="detail-back"><ChevronLeft className="w-4 h-4" /></button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold" data-testid="audit-title">{audit.title}</h1>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase border ${STATUS_COLORS[audit.status] || ""}`} data-testid="audit-status-badge">{audit.status?.replace("_", " ")}</span>
                <span className={`text-[10px] font-bold ${RISK_COLORS[audit.risk_level] || ""}`}>{audit.risk_level?.toUpperCase()}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{audit.audit_id} &middot; {audit.audit_type} &middot; {(audit.standards || []).join(", ")}</div>
            </div>
          </div>
          <StatusStepper audit={audit} onUpdate={loadAudit} />
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-[#1E3A5F]/30 px-6">
        <div className="max-w-6xl mx-auto flex gap-1 overflow-x-auto" data-testid="audit-tabs">
          {tabs.map(tabItem => (
            <button key={tabItem.id} onClick={() => setActiveTab(tabItem.id)} className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-bold whitespace-nowrap border-b-2 transition-colors ${activeTab === tabItem.id ? "border-[#1A6BCC] text-[#00C8E0]" : "border-transparent text-slate-500 hover:text-white"}`} data-testid={`tab-${tabItem.id}`}>
              <tabItem.icon className="w-3.5 h-3.5" /> {tabItem.label}
            </button>
          ))}
        </div>
      </div>
      {/* Tab content */}
      <div className="max-w-6xl mx-auto p-6">
        {activeTab === "overview" && <OverviewTab audit={audit} />}
        {activeTab === "checklist" && <ChecklistTab auditId={auditId} audit={audit} onUpdate={loadAudit} />}
        {activeTab === "evidence" && <EvidenceTab auditId={auditId} />}
        {activeTab === "findings" && <FindingsTab auditId={auditId} />}
        {activeTab === "front_room" && <FrontRoomTab auditId={auditId} />}
        {activeTab === "back_room" && <BackRoomTab auditId={auditId} />}
        {activeTab === "daily_wrap" && <DailyWrapTab auditId={auditId} />}
        {activeTab === "packages" && <PackagesTab auditId={auditId} />}
        {activeTab === "transfers" && <TransfersTab auditId={auditId} />}
      </div>
    </div>
  );
};

/* ═══════════ STATUS STEPPER ═══════════ */
const StatusStepper = ({ audit, onUpdate }) => {
  const [updating, setUpdating] = useState(false);
  const nextStatus = () => {
    const idx = AUDIT_STATUSES.indexOf(audit.status);
    if (idx < AUDIT_STATUSES.length - 1) return AUDIT_STATUSES[idx + 1];
    return null;
  };
  const advance = async () => {
    const ns = nextStatus();
    if (!ns) return;
    setUpdating(true);
    try {
      await axios.put(`${API}/audit-conductor/audits/${audit.audit_id}`, { status: ns }, { withCredentials: true });
      onUpdate();
    } catch (e) { console.error(e); }
    finally { setUpdating(false); }
  };
  const ns = nextStatus();
  if (!ns) return null;
  return (
    <button onClick={advance} disabled={updating} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A6BCC] hover:bg-[#1558A8] rounded-lg text-xs font-bold disabled:opacity-50" data-testid="advance-status-btn">
      {updating ? <Loader2 className="w-3 h-3 animate-spin" /> : <ArrowRight className="w-3 h-3" />} Advance to {ns.replace("_", " ")}
    </button>
  );
};

/* ═══════════ OVERVIEW TAB ═══════════ */
const OverviewTab = ({ audit }) => {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    axios.get(`${API}/audit-conductor/audits/${audit.audit_id}/summary`, { withCredentials: true })
      .then(r => setSummary(r.data)).catch(() => {});
  }, [audit.audit_id]);

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label={t('conductor.overview.checklistItems')} value={summary?.checklist?.total || 0} sub={`${summary?.open_checklist_items || 0} ${t('conductor.overview.open')}`} />
        <StatCard label={t('conductor.overview.findings')} value={summary?.findings?.total || 0} sub={`${summary?.open_findings || 0} ${t('conductor.overview.open')}`} />
        <StatCard label={t('conductor.overview.backRoomTasks')} value={summary?.back_room?.total || 0} sub={`${summary?.open_back_room_tasks || 0} ${t('conductor.overview.open')}`} />
      </div>
      <div className="bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-xl p-4 space-y-2">
        <div className="text-xs text-slate-400 font-bold uppercase">{t('conductor.overview.auditDetails')}</div>
        {audit.scope && <Row label={t('conductor.overview.scope')} value={audit.scope} />}
        {audit.site && <Row label={t('conductor.overview.site')} value={audit.site} />}
        {audit.product_family && <Row label={t('conductor.overview.productFamily')} value={audit.product_family} />}
        <Row label={t('conductor.overview.standards')} value={(audit.standards || []).join(", ")} />
        <Row label={t('conductor.overview.rooms')} value={`${audit.front_room_enabled ? t('conductor.tabs.frontRoom') : ""} ${audit.back_room_enabled ? t('conductor.tabs.backRoom') : ""}`} />
        {audit.scheduled_start && <Row label={t('conductor.overview.scheduled')} value={`${audit.scheduled_start} — ${audit.scheduled_end || t('conductor.overview.tbd')}`} />}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, sub }) => (
  <div className="bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-xl p-4">
    <div className="text-2xl font-bold text-white">{value}</div>
    <div className="text-xs text-slate-400 mt-1">{label}</div>
    {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex">
    <span className="text-[10px] text-slate-500 w-28 shrink-0 uppercase">{label}</span>
    <span className="text-xs text-slate-300">{value}</span>
  </div>
);

/* ═══════════ CHECKLIST TAB ═══════════ */
const ChecklistTab = ({ auditId, audit, onUpdate }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filterStd, setFilterStd] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filterStd ? `?standard=${encodeURIComponent(filterStd)}` : "";
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}/checklist${q}`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [auditId, filterStd]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/checklist/generate`, {}, { withCredentials: true });
      load(); onUpdate();
    } catch (e) { alert(e.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const updateItem = async (itemId, data) => {
    try {
      await axios.put(`${API}/audit-conductor/checklist-items/${itemId}`, data, { withCredentials: true });
      load();
    } catch (e) { console.error(e); }
  };

  const launchScriber = (item) => {
    const ctx = { audit_id: auditId, checklist_item_id: item.item_id, standard: item.standard, clause: item.clause };
    const params = new URLSearchParams(ctx).toString();
    window.open(`/scriber?${params}`, "_blank");
  };

  if (loading) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading checklist...</div>;

  if (items.length === 0 && !audit.checklist_generated) {
    return (
      <div className="text-center py-12" data-testid="checklist-empty">
        <ClipboardList className="w-10 h-10 text-[#1E3A5F] mx-auto mb-3" />
        <p className="text-sm text-slate-400 mb-4">Checklist not generated yet.</p>
        <button onClick={generate} disabled={generating} className="px-6 py-2.5 bg-[#1A6BCC] rounded-lg text-sm font-bold disabled:opacity-50" data-testid="generate-checklist-btn">
          {generating ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <Zap className="w-4 h-4 inline mr-2" />}Generate Checklist
        </button>
      </div>
    );
  }

  const standards = [...new Set(items.map(i => i.standard))];

  return (
    <div data-testid="checklist-tab">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{items.length} items</span>
          <select value={filterStd} onChange={e => setFilterStd(e.target.value)} className="bg-[#132340] border border-[#1E3A5F] rounded px-2 py-1 text-[10px] text-white" data-testid="checklist-filter">
            <option value="">{t('conductor.allStandards')}</option>
            {standards.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="space-y-1">
        {items.map(item => (
          <div key={item.item_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg" data-testid={`cli-${item.item_id}`}>
            <button onClick={() => setExpanded(expanded === item.item_id ? null : item.item_id)} className="w-full text-left px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <StatusDot status={item.status} />
                <div className="min-w-0">
                  <div className="text-xs font-bold text-white truncate">{item.standard} {item.clause} — {item.title}</div>
                  <div className="text-[10px] text-slate-500 truncate">{item.question}</div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0 ml-2">
                <span className={`text-[9px] font-bold ${item.risk_tag === "critical" ? "text-red-400" : item.risk_tag === "high" ? "text-orange-400" : "text-slate-500"}`}>{item.risk_tag}</span>
                {expanded === item.item_id ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
              </div>
            </button>
            {expanded === item.item_id && (
              <div className="px-4 pb-3 border-t border-[#1E3A5F]/20 pt-3 space-y-3">
                <div className="text-xs text-slate-300"><span className="text-slate-500">Question:</span> {item.question}</div>
                <div className="text-xs text-slate-300"><span className="text-slate-500">Expected Evidence:</span> {item.expected_evidence}</div>
                <div className="text-xs text-slate-300"><span className="text-slate-500">Lifecycle:</span> {item.lifecycle_stage} | <span className="text-slate-500">Severity if failed:</span> {item.severity_if_failed}</div>
                {item.notes && <div className="text-xs text-slate-400 bg-[#0A1628] rounded p-2">{item.notes}</div>}
                <div className="flex gap-2 flex-wrap">
                  {CHECKLIST_ITEM_STATUSES.filter(s => s !== item.status).map(s => (
                    <button key={s} onClick={() => updateItem(item.item_id, { status: s })} className="px-2 py-1 bg-[#132340] hover:bg-[#1E3A5F] rounded text-[10px] text-slate-300" data-testid={`set-status-${s}`}>{s.replace("_", " ")}</button>
                  ))}
                  <button onClick={() => launchScriber(item)} className="px-2 py-1 bg-purple-600/30 hover:bg-purple-600/50 rounded text-[10px] text-purple-300 flex items-center gap-1" data-testid="launch-scriber-btn">
                    <Pen className="w-3 h-3" /> SCRIBER
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const StatusDot = ({ status }) => {
  const colors = { not_started: "bg-slate-500", in_progress: "bg-blue-400", complete: "bg-emerald-400", concern_raised: "bg-amber-400", finding_raised: "bg-red-400", deferred: "bg-slate-600" };
  return <div className={`w-2 h-2 rounded-full shrink-0 ${colors[status] || "bg-slate-500"}`} />;
};

/* ═══════════ EVIDENCE TAB ═══════════ */
const EvidenceTab = ({ auditId }) => {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", evidence_type: "note", content: "", confidentiality: "internal", source_room: "front_room" });

  useEffect(() => {
    axios.get(`${API}/audit-conductor/audits/${auditId}/evidence`, { withCredentials: true })
      .then(r => { setEvidence(r.data.evidence || []); setLoading(false); }).catch(() => setLoading(false));
  }, [auditId]);

  const addEvidence = async () => {
    if (!form.title) return;
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/evidence`, form, { withCredentials: true });
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}/evidence`, { withCredentials: true });
      setEvidence(res.data.evidence || []);
      setShowForm(false);
      setForm({ title: "", description: "", evidence_type: "note", content: "", confidentiality: "internal", source_room: "front_room" });
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;

  return (
    <div data-testid="evidence-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{evidence.length} evidence items</span>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A6BCC] rounded-lg text-xs font-bold" data-testid="add-evidence-btn"><Plus className="w-3 h-3" /> Add Evidence</button>
      </div>
      {showForm && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 mb-4 space-y-3" data-testid="evidence-form">
          <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Evidence title" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="evidence-title" />
          <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Content or description" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white h-20 resize-none" data-testid="evidence-content" />
          <div className="flex gap-2">
            <select value={form.evidence_type} onChange={e => setForm(p => ({ ...p, evidence_type: e.target.value }))} className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white" data-testid="evidence-type-select">
              {["note", "document_upload", "screenshot", "link", "photo", "record_reference"].map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            <select value={form.confidentiality} onChange={e => setForm(p => ({ ...p, confidentiality: e.target.value }))} className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white">
              {["public", "internal", "confidential", "restricted"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button onClick={addEvidence} className="px-4 py-2 bg-emerald-600 rounded-lg text-xs font-bold" data-testid="save-evidence-btn">Save</button>
        </div>
      )}
      <div className="space-y-2">
        {evidence.map(e => (
          <div key={e.evidence_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg p-3" data-testid={`evidence-${e.evidence_id}`}>
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-white">{e.title}</div>
              <div className="flex items-center gap-2">
                <span className="text-[9px] text-slate-500">{e.evidence_type}</span>
                <span className={`text-[9px] ${e.confidentiality === "restricted" ? "text-red-400" : "text-slate-500"}`}>{e.confidentiality}</span>
              </div>
            </div>
            {e.description && <div className="text-[10px] text-slate-400 mt-1">{e.description}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════ FINDINGS TAB ═══════════ */
const FindingsTab = ({ auditId }) => {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ finding_type: "minor_nonconformance", standard: "", clause: "", condition_statement: "", objective_evidence: "", risk_impact: "", affected_area: "", owner: "", containment_required: false, capa_required: false });
  const [expandedRec, setExpandedRec] = useState(null);
  const [recs, setRecs] = useState({});
  const [loadingRecs, setLoadingRecs] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}/findings`, { withCredentials: true });
      setFindings(res.data.findings || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [auditId]);

  useEffect(() => { load(); }, [load]);

  const addFinding = async () => {
    if (!form.condition_statement) return;
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/findings`, form, { withCredentials: true });
      load(); setShowForm(false);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const createAction = async (findingId) => {
    const desc = window.prompt("Action description:");
    if (!desc) return;
    try {
      await axios.post(`${API}/audit-conductor/findings/${findingId}/actions`, {
        finding_id: findingId, action_type: "capa", description: desc, owner: "Quality Manager",
      }, { withCredentials: true });
      alert("Action created");
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const loadRecommendations = async (findingId) => {
    if (expandedRec === findingId) { setExpandedRec(null); return; }
    setExpandedRec(findingId);
    if (recs[findingId]) return;
    setLoadingRecs(p => ({ ...p, [findingId]: true }));
    try {
      const res = await axios.get(`${API}/audit-conductor/findings/${findingId}/defense-recommendations`, { withCredentials: true });
      setRecs(p => ({ ...p, [findingId]: res.data.recommendations || [] }));
    } catch (e) { console.error(e); }
    setLoadingRecs(p => ({ ...p, [findingId]: false }));
  };

  const applyScript = async (findingId, scenarioId) => {
    try {
      await axios.post(`${API}/audit-conductor/findings/${findingId}/defense-recommendations/${scenarioId}/apply`, {}, { withCredentials: true });
      setRecs(p => ({ ...p, [findingId]: (p[findingId] || []).map(r => r.scenario_id === scenarioId ? { ...r, already_applied: true } : r) }));
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;

  return (
    <div data-testid="findings-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{findings.length} findings</span>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 rounded-lg text-xs font-bold" data-testid="add-finding-btn"><Plus className="w-3 h-3" /> New Finding</button>
      </div>
      {showForm && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 mb-4 space-y-3" data-testid="finding-form">
          <select value={form.finding_type} onChange={e => setForm(p => ({ ...p, finding_type: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="finding-type-select">
            {FINDING_TYPES.map(ftype => <option key={ftype} value={ftype}>{ftype.replace(/_/g, " ")}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input value={form.standard} onChange={e => setForm(p => ({ ...p, standard: e.target.value }))} placeholder="Standard" className="bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="finding-standard" />
            <input value={form.clause} onChange={e => setForm(p => ({ ...p, clause: e.target.value }))} placeholder="Clause" className="bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="finding-clause" />
          </div>
          <textarea value={form.condition_statement} onChange={e => setForm(p => ({ ...p, condition_statement: e.target.value }))} placeholder="Condition statement *" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white h-20 resize-none" data-testid="finding-condition" />
          <textarea value={form.objective_evidence} onChange={e => setForm(p => ({ ...p, objective_evidence: e.target.value }))} placeholder="Objective evidence" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white h-16 resize-none" data-testid="finding-evidence" />
          <div className="flex gap-3">
            <label className="flex items-center gap-1 text-xs text-slate-300"><input type="checkbox" checked={form.capa_required} onChange={e => setForm(p => ({ ...p, capa_required: e.target.checked }))} /> CAPA Required</label>
            <label className="flex items-center gap-1 text-xs text-slate-300"><input type="checkbox" checked={form.containment_required} onChange={e => setForm(p => ({ ...p, containment_required: e.target.checked }))} /> Containment</label>
          </div>
          <button onClick={addFinding} className="px-4 py-2 bg-orange-600 rounded-lg text-xs font-bold" data-testid="save-finding-btn">Save Finding</button>
        </div>
      )}
      <div className="space-y-2">
        {findings.map(f => (
          <div key={f.finding_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg p-3" data-testid={`finding-${f.finding_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${FINDING_TYPE_COLORS[f.finding_type] || ""}`}>{f.finding_type?.replace(/_/g, " ")}</span>
                <span className="text-[10px] text-slate-500">{f.standard} {f.clause}</span>
              </div>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${STATUS_COLORS[f.status] || "bg-slate-500/20 text-slate-400"}`}>{f.status}</span>
            </div>
            <div className="text-xs text-slate-300">{f.condition_statement}</div>
            {f.capa_required && <div className="text-[10px] text-orange-400 mt-1">CAPA Required</div>}
            <div className="flex items-center gap-2 mt-2">
              <button onClick={() => createAction(f.finding_id)} className="px-2 py-1 bg-[#132340] hover:bg-[#1E3A5F] rounded text-[10px] text-slate-300" data-testid="create-action-btn">Create Action/CAPA</button>
              <button
                onClick={() => loadRecommendations(f.finding_id)}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] transition-colors ${
                  expandedRec === f.finding_id ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/30" : "bg-[#132340] hover:bg-[#1E3A5F] text-slate-300"
                }`}
                data-testid={`defense-rec-btn-${f.finding_id}`}
              >
                <Shield className="w-3 h-3" /> Defense Scripts
                {expandedRec === f.finding_id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
            {/* Defense Recommendations Panel */}
            {expandedRec === f.finding_id && (
              <div className="mt-3 border-t border-[#1E3A5F]/30 pt-3" data-testid={`defense-recs-panel-${f.finding_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-3.5 h-3.5 text-cyan-400" />
                  <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-wide">Defense Script Recommendations</span>
                </div>
                {loadingRecs[f.finding_id] ? (
                  <div className="flex items-center gap-2 py-3"><Loader2 className="w-3 h-3 animate-spin text-cyan-400" /><span className="text-[10px] text-slate-400">Matching scripts...</span></div>
                ) : (recs[f.finding_id] || []).length === 0 ? (
                  <div className="text-[10px] text-slate-500 py-2">No matching defense scripts found for this finding.</div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                    {(recs[f.finding_id] || []).map(r => (
                      <div key={r.scenario_id} className={`bg-[#0A1628] border rounded-lg p-2.5 ${r.match_level === "strong" ? "border-cyan-500/30" : r.match_level === "moderate" ? "border-amber-500/20" : "border-[#1E3A5F]/30"}`} data-testid={`rec-${r.scenario_id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-[10px] font-semibold text-white truncate">{r.key_defense_term}</span>
                            <span className={`px-1 py-0.5 rounded text-[8px] font-bold ${r.match_level === "strong" ? "bg-cyan-400/15 text-cyan-400" : r.match_level === "moderate" ? "bg-amber-400/15 text-amber-400" : "bg-slate-400/15 text-slate-400"}`}>{r.match_score}pt</span>
                            <span className={`px-1 py-0.5 rounded text-[8px] ${r.severity === "HIGH" ? "bg-red-400/15 text-red-400" : r.severity === "MEDIUM" ? "bg-amber-400/15 text-amber-400" : "bg-emerald-400/15 text-emerald-400"}`}>{r.severity}</span>
                          </div>
                          {r.already_applied ? (
                            <span className="text-[9px] text-emerald-400 font-semibold flex items-center gap-0.5" data-testid={`applied-${r.scenario_id}`}><CheckCircle2 className="w-3 h-3" /> Applied</span>
                          ) : (
                            <button
                              onClick={() => applyScript(f.finding_id, r.scenario_id)}
                              className="text-[9px] text-cyan-400 hover:text-cyan-300 font-semibold px-1.5 py-0.5 rounded bg-cyan-400/10 hover:bg-cyan-400/20 transition-colors"
                              data-testid={`apply-${r.scenario_id}`}
                            >Apply</button>
                          )}
                        </div>
                        <div className="text-[10px] text-slate-400 mb-1">{r.scenario}</div>
                        <div className="text-[9px] text-cyan-300/70 italic">{r.recommended_response}</div>
                        {r.front_room_talking_points?.length > 0 && (
                          <div className="mt-1.5">
                            <span className="text-[8px] text-slate-500 uppercase font-semibold">Talking Points:</span>
                            <ul className="mt-0.5 space-y-0.5">
                              {r.front_room_talking_points.map((tp, i) => (
                                <li key={i} className="text-[9px] text-slate-400 pl-2 relative before:content-[''] before:absolute before:left-0 before:top-[5px] before:w-1 before:h-1 before:rounded-full before:bg-cyan-400/40">{tp}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════ FRONT ROOM TAB ═══════════ */
const FrontRoomTab = ({ auditId }) => {
  const [logs, setLogs] = useState([]);
  const [liveFeed, setLiveFeed] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ checklist_item_id: "", auditor_question: "", auditee_response: "", posture: "neutral" });
  const [showForm, setShowForm] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    try {
      const [logsRes, liveRes] = await Promise.all([
        axios.get(`${API}/audit-conductor/audits/${auditId}/front-room`, { withCredentials: true }),
        axios.get(`${API}/audit-conductor/audits/${auditId}/front-room/live`, { withCredentials: true }),
      ]);
      setLogs(logsRes.data.logs || []);
      setLiveFeed(liveRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [auditId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh polling for live collaboration
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, load]);

  const addLog = async () => {
    if (!form.auditor_question) return;
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/front-room`, form, { withCredentials: true });
      load();
      setShowForm(false);
      setForm({ checklist_item_id: "", auditor_question: "", auditee_response: "", posture: "neutral" });
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const requestSupport = async (logId) => {
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/front-room/${logId}/request-support`, {}, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const launchScriber = () => {
    const params = new URLSearchParams({ audit_id: auditId, room_mode: "front_room" }).toString();
    window.open(`/scriber?${params}`, "_blank");
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;

  return (
    <div data-testid="front-room-tab">
      {/* Live status bar */}
      {liveFeed && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="live-feed-bar">
          <div className="flex items-center gap-3">
            <div className={`w-2 h-2 rounded-full ${autoRefresh ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
            <span className="text-xs text-white font-bold">Live Feed</span>
            <span className="text-[10px] text-slate-500">{liveFeed.recent_logs?.length || 0} entries &middot; {liveFeed.active_back_room_tasks?.length || 0} active back room tasks</span>
            {liveFeed.participants?.length > 0 && (
              <span className="text-[10px] text-slate-500">&middot; {liveFeed.participants.length} participants</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setAutoRefresh(!autoRefresh)} className={`px-2 py-1 rounded text-[10px] font-bold ${autoRefresh ? "bg-emerald-500/20 text-emerald-400" : "bg-[#132340] text-slate-400"}`} data-testid="toggle-auto-refresh">
              {autoRefresh ? t('conductor.liveOn') : t('conductor.liveOff')}
            </button>
            <button onClick={launchScriber} className="flex items-center gap-1 px-2 py-1 bg-purple-600/30 rounded text-[10px] text-purple-300" data-testid="fr-launch-scriber">
              <Pen className="w-3 h-3" /> SCRIBER
            </button>
            <button onClick={load} className="p-1 rounded bg-[#132340] hover:bg-[#1E3A5F]" data-testid="refresh-feed"><RefreshCw className="w-3 h-3 text-slate-400" /></button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{logs.length} {t('conductor.logs')}</span>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A6BCC] rounded-lg text-xs font-bold" data-testid="add-front-room-btn"><Plus className="w-3 h-3" /> {t('conductor.logEntry')}</button>
      </div>
      {showForm && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 mb-4 space-y-3" data-testid="front-room-form">
          <input value={form.auditor_question} onChange={e => setForm(p => ({ ...p, auditor_question: e.target.value }))} placeholder={t('conductor.auditorQuestion')} className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="fr-question" />
          <textarea value={form.auditee_response} onChange={e => setForm(p => ({ ...p, auditee_response: e.target.value }))} placeholder={t('conductor.auditeeResponse')} className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white h-20 resize-none" data-testid="fr-response" />
          <select value={form.posture} onChange={e => setForm(p => ({ ...p, posture: e.target.value }))} className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white" data-testid="fr-posture">
            {["neutral", "satisfied", "concern", "escalating", "finding_likely"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={addLog} className="px-4 py-2 bg-emerald-600 rounded-lg text-xs font-bold" data-testid="save-fr-btn">Save</button>
        </div>
      )}
      <div className="space-y-2">
        {logs.map(l => (
          <div key={l.log_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg p-3" data-testid={`fr-log-${l.log_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={`text-[9px] font-bold ${l.posture === "finding_likely" ? "text-red-400" : l.posture === "escalating" ? "text-orange-400" : l.posture === "concern" ? "text-amber-400" : l.posture === "satisfied" ? "text-emerald-400" : "text-slate-400"}`}>{l.posture}</span>
                <span className="text-[10px] text-slate-600">{new Date(l.created_at).toLocaleString()}</span>
              </div>
              <button onClick={() => requestSupport(l.log_id)} className="flex items-center gap-1 px-2 py-0.5 bg-amber-500/10 hover:bg-amber-500/20 rounded text-[9px] text-amber-400" data-testid="request-support-btn">
                <Users className="w-3 h-3" /> Back Room
              </button>
            </div>
            <div className="text-xs text-white mb-1"><strong>Q:</strong> {l.auditor_question}</div>
            {l.auditee_response && <div className="text-xs text-slate-400"><strong>A:</strong> {l.auditee_response}</div>}
            {l.linked_back_room_task_ids?.length > 0 && (
              <div className="text-[10px] text-amber-400 mt-1">{l.linked_back_room_task_ids.length} support request(s)</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════ BACK ROOM TAB ═══════════ */
const BackRoomTab = ({ auditId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: "", priority: "medium", sme_owner: "" });

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}/back-room`, { withCredentials: true });
      setTasks(res.data.tasks || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [auditId]);

  useEffect(() => { load(); }, [load]);

  const addTask = async () => {
    if (!form.description) return;
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/back-room`, form, { withCredentials: true });
      load(); setShowForm(false);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  const updateTask = async (taskId, data) => {
    try {
      await axios.put(`${API}/audit-conductor/back-room/${taskId}`, data, { withCredentials: true });
      load();
    } catch (e) { console.error(e); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;

  return (
    <div data-testid="back-room-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{tasks.length} tasks</span>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A6BCC] rounded-lg text-xs font-bold" data-testid="add-back-room-btn"><Plus className="w-3 h-3" /> New Task</button>
      </div>
      {showForm && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 mb-4 space-y-3" data-testid="back-room-form">
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Task description *" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white h-20 resize-none" data-testid="br-description" />
          <div className="flex gap-2">
            <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white" data-testid="br-priority">
              {["low", "medium", "high", "critical"].map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <input value={form.sme_owner} onChange={e => setForm(p => ({ ...p, sme_owner: e.target.value }))} placeholder="SME Owner" className="bg-[#0A1628] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white flex-1" data-testid="br-sme" />
          </div>
          <button onClick={addTask} className="px-4 py-2 bg-emerald-600 rounded-lg text-xs font-bold" data-testid="save-br-btn">Save</button>
        </div>
      )}
      <div className="space-y-2">
        {tasks.map(task => (
          <div key={task.task_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg p-3" data-testid={`br-task-${task.task_id}`}>
            <div className="flex items-center justify-between mb-1">
              <span className={`text-[9px] font-bold ${task.priority === "critical" ? "text-red-400" : task.priority === "high" ? "text-orange-400" : "text-slate-400"}`}>{task.priority}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${STATUS_COLORS[task.status] || "bg-slate-500/20 text-slate-400"}`}>{task.status}</span>
            </div>
            <div className="text-xs text-white">{task.description}</div>
            {task.sme_owner && <div className="text-[10px] text-slate-500 mt-1">SME: {task.sme_owner}</div>}
            <div className="flex gap-1.5 mt-2">
              {["in_progress", "submitted", "closed"].filter(s => s !== task.status).map(s => (
                <button key={s} onClick={() => updateTask(task.task_id, { status: s })} className="px-2 py-0.5 bg-[#132340] hover:bg-[#1E3A5F] rounded text-[9px] text-slate-400">{s.replace("_", " ")}</button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════ DAILY WRAP TAB ═══════════ */
const DailyWrapTab = ({ auditId }) => {
  const [summary, setSummary] = useState(null);
  const [wrapping, setWrapping] = useState(false);

  const createWrap = async () => {
    setWrapping(true);
    try {
      const res = await axios.post(`${API}/audit-conductor/audits/${auditId}/daily-wrap`, {}, { withCredentials: true });
      setSummary(res.data);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setWrapping(false); }
  };

  useEffect(() => {
    axios.get(`${API}/audit-conductor/audits/${auditId}/summary`, { withCredentials: true })
      .then(r => setSummary(r.data)).catch(() => {});
  }, [auditId]);

  return (
    <div data-testid="daily-wrap-tab">
      <button onClick={createWrap} disabled={wrapping} className="mb-4 px-4 py-2 bg-amber-600 rounded-lg text-xs font-bold disabled:opacity-50" data-testid="create-wrap-btn">
        {wrapping ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <Clock className="w-3 h-3 inline mr-1" />}Create Daily Wrap
      </button>
      {summary && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 space-y-3" data-testid="wrap-summary">
          {summary.summary_text && <div className="text-sm text-white">{summary.summary_text}</div>}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center"><div className="text-lg font-bold text-white">{summary.open_findings ?? summary.findings?.total ?? 0}</div><div className="text-[10px] text-slate-400">Open Findings</div></div>
            <div className="text-center"><div className="text-lg font-bold text-white">{summary.open_back_room_tasks ?? summary.back_room?.total ?? 0}</div><div className="text-[10px] text-slate-400">Open Back Room</div></div>
            <div className="text-center"><div className="text-lg font-bold text-white">{summary.open_checklist_items ?? summary.checklist?.total ?? 0}</div><div className="text-[10px] text-slate-400">Open Checklist</div></div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════ PACKAGES TAB ═══════════ */
const PackagesTab = ({ auditId }) => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}/packages`, { withCredentials: true });
      setPackages(res.data.packages || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [auditId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/audit-conductor/audits/${auditId}/packages`, { package_type: "audit_report" }, { withCredentials: true });
      load();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
    finally { setGenerating(false); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;

  return (
    <div data-testid="packages-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{packages.length} packages</span>
        <button onClick={generate} disabled={generating} className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 rounded-lg text-xs font-bold disabled:opacity-50" data-testid="generate-package-btn">
          {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Package className="w-3 h-3" />} Generate Package
        </button>
      </div>
      <div className="space-y-2">
        {packages.map(p => (
          <div key={p.package_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg p-3" data-testid={`pkg-${p.package_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-white">{p.package_id}</div>
                <div className="text-[10px] text-slate-500">{p.package_type} &middot; v{p.version} &middot; {new Date(p.generated_at).toLocaleString()}</div>
              </div>
              <div className="text-[10px] text-slate-400">{p.included_objects?.checklist_items || 0} items, {p.included_objects?.findings || 0} findings, {p.included_objects?.evidence || 0} evidence</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ═══════════ TRANSFERS TAB ═══════════ */
const TransfersTab = ({ auditId }) => {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ package_id: "", recipient_name: "", recipient_email: "", recipient_org: "", transfer_method: "platform", reason: "", chain_of_custody_note: "" });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/audit-conductor/audits/${auditId}/transfers`, { withCredentials: true }),
      axios.get(`${API}/audit-conductor/audits/${auditId}/packages`, { withCredentials: true }),
    ]).then(([t, p]) => {
      setTransfers(t.data.transfers || []);
      setPackages(p.data.packages || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [auditId]);

  const transfer = async () => {
    if (!form.package_id || !form.recipient_name) return;
    try {
      await axios.post(`${API}/audit-conductor/transfers`, form, { withCredentials: true });
      const res = await axios.get(`${API}/audit-conductor/audits/${auditId}/transfers`, { withCredentials: true });
      setTransfers(res.data.transfers || []);
      setShowForm(false);
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <Loader2 className="w-4 h-4 animate-spin text-slate-400" />;

  return (
    <div data-testid="transfers-tab">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-slate-400">{transfers.length} transfers</span>
        <button onClick={() => setShowForm(!showForm)} className="flex items-center gap-1 px-3 py-1.5 bg-[#1A6BCC] rounded-lg text-xs font-bold" data-testid="transfer-btn"><Send className="w-3 h-3" /> Transfer</button>
      </div>
      {showForm && (
        <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-xl p-4 mb-4 space-y-3" data-testid="transfer-form">
          <select value={form.package_id} onChange={e => setForm(p => ({ ...p, package_id: e.target.value }))} className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="transfer-package-select">
            <option value="">Select package</option>
            {packages.map(p => <option key={p.package_id} value={p.package_id}>{p.package_id} (v{p.version})</option>)}
          </select>
          <input value={form.recipient_name} onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))} placeholder="Recipient name *" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="transfer-recipient" />
          <input value={form.recipient_email} onChange={e => setForm(p => ({ ...p, recipient_email: e.target.value }))} placeholder="Email" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="transfer-email" />
          <input value={form.reason} onChange={e => setForm(p => ({ ...p, reason: e.target.value }))} placeholder="Reason" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white" data-testid="transfer-reason" />
          <textarea value={form.chain_of_custody_note} onChange={e => setForm(p => ({ ...p, chain_of_custody_note: e.target.value }))} placeholder="Chain of custody note" className="w-full bg-[#0A1628] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white h-16 resize-none" data-testid="transfer-custody" />
          <button onClick={transfer} className="px-4 py-2 bg-emerald-600 rounded-lg text-xs font-bold" data-testid="save-transfer-btn">Transfer</button>
        </div>
      )}
      <div className="space-y-2">
        {transfers.map(xfer => (
          <div key={xfer.transfer_id} className="bg-[#0D1F38] border border-[#1E3A5F]/30 rounded-lg p-3" data-testid={`transfer-${xfer.transfer_id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-bold text-white">{xfer.recipient_name} ({xfer.recipient_org || "N/A"})</div>
                <div className="text-[10px] text-slate-500">{xfer.package_id} via {xfer.transfer_method} &middot; {new Date(xfer.transferred_at).toLocaleString()}</div>
              </div>
            </div>
            {xfer.chain_of_custody_note && <div className="text-[10px] text-slate-400 mt-1">{xfer.chain_of_custody_note}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};

const FINDING_TYPES = ["observation", "ofi", "minor_nonconformance", "major_nonconformance", "critical_nonconformance"];
const CHECKLIST_ITEM_STATUSES = ["not_started", "in_progress", "complete", "concern_raised", "finding_raised", "deferred"];

export default AuditConductorPage;
